// src/services/wellIntelService.js
// FlowSense MOW+ — Well & Drainage Intelligence API client (backend: routes/wells.js)
import api from './api';

const unwrap = (res) => {
  const body = res?.data;
  if (body && body.success === false) throw new Error(body.message || 'Well intelligence request failed');
  return body?.data ?? body;
};

/** Every well in the register with intelligence + fleet summary: { wells, summary, generated_at }. */
export const fetchWellFleetIntel = async ({ force = false, signal } = {}) =>
  unwrap(await api.get('/wells/intel', { params: force ? { force: 1 } : undefined, signal }));

/** One well, freshly computed. */
export const fetchWellIntel = async (wellId, { signal } = {}) =>
  unwrap(await api.get(`/wells/${encodeURIComponent(wellId)}/intel`, { signal }));

/** Recompute after an inspection / work order change; the server also pushes `well_intel_update`. */
export const refreshWellIntel = async (wellId) =>
  unwrap(await api.post(`/wells/${encodeURIComponent(wellId)}/intel/refresh`));

// --- UPDATE (live work orders): drafts are now valid for POST /workorders as they are.
// Before, every draft carried inspection_source 'flowsense_well_intel' (or 'flowsense_flood_alert'), which
// the backend validator rejects (allowed: work_order, road_inspection, road_assessment, well_clearance,
// asset_inspection, sensor_check, other), and had no domain / type, so the job was not filed as well work.
// Each FlowSense recommendation now maps to the work order catalog (domain · type · category).
const REC_PLAN = Object.freeze({
  DISPATCH_CREW: { type: 'emergency', category: 'Flood Response' },
  CLEAR_DRAINAGE: { type: 'corrective', category: 'Well Clearance / Desilting' },
  INSPECT_DRAINAGE: { type: 'inspection', category: 'Siltation Survey', inspection: true },
  SCHEDULE_INSPECTION: { type: 'inspection', category: 'Siltation Survey', inspection: true },
  DESILT_WELL: { type: 'preventive', category: 'Well Clearance / Desilting' },
  SCHEDULE_CLEANING: { type: 'preventive', category: 'Well Clearance / Desilting' },
  REPAIR_CASING: { type: 'corrective' },
  INSTALL_SENSOR: { type: 'installation', category: 'Sensor Installation' },
  CHECK_SENSOR: { type: 'corrective', category: 'Connectivity Fault', sensor: true },
  REVIEW_ALERTS: { type: 'corrective' },
  MAP_DRAINAGE: { type: 'inspection', inspection: true },
});
const REC_TITLES = {
  DISPATCH_CREW: (n) => `Flood response — ${n}`,
  CLEAR_DRAINAGE: (n) => `Clear drainage serving ${n}`,
  INSPECT_DRAINAGE: (n) => `Inspect drainage serving ${n}`,
  SCHEDULE_INSPECTION: (n) => `Inspect ${n}`,
  SCHEDULE_CLEANING: (n) => `Silt cleaning — ${n}`,
  DESILT_WELL: (n) => `Desilt ${n}`,
  REPAIR_CASING: (n) => `Casing repair — ${n}`,
  INSTALL_SENSOR: (n) => `Install water-level sensor at ${n}`,
  CHECK_SENSOR: (n) => `Sensor offline — ${n}`,
  REVIEW_ALERTS: (n) => `Review active alerts — ${n}`,
  MAP_DRAINAGE: (n) => `Survey drainage outlet for ${n}`,
};
const PRIORITY_OF = { urgent: 'critical', critical: 'critical', high: 'high', medium: 'medium', low: 'low' };
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const sensorOfWell = (well) => well?.monitoring?.primary_device_id || (well?.sensors || [])[0]?.device_id || null;

/**
 * Work order draft for a well + one FlowSense recommendation. Valid for POST /workorders as is, and still
 * understood by WorkOrderPage (navigate('/admin/workorders', { state: { woDraft } })).
 * Fields starting with "_" are display-only and are never sent.
 */
export const buildWellWorkOrderDraft = (well, recommendation) => {
  const name = well?.name || `Well ${well?.id}`;
  const rec = recommendation || well?.intel?.recommendations?.[0] || null;
  const plan = REC_PLAN[rec?.code] || { type: 'corrective' };
  const deviceId = sensorOfWell(well);
  const sensorJob = !!(plan.sensor && deviceId); // CHECK_SENSOR → a sensor job when the device is known
  const drivers = (well?.intel?.drivers || []).slice(0, 4).map((d) => `• ${d.label}: ${d.evidence}`).join('\n');
  return {
    title: (REC_TITLES[rec?.code] || ((n) => `Well follow-up — ${n}`))(name),
    priority: PRIORITY_OF[rec?.priority] || (plan.type === 'emergency' ? 'critical' : 'medium'),
    domain: sensorJob ? 'sensor' : 'well',
    work_order_type: plan.type,
    category: plan.category,
    source: 'ai_prescriptive',
    asset_id: well?.id != null ? String(well.id) : undefined, // wells are assets: the job shows on the well everywhere
    well_id: well?.id != null ? String(well.id) : undefined,
    sensor_id: deviceId ? String(deviceId) : undefined, // the well's sensor, so the job also shows on the sensor
    inspection_source: plan.inspection ? 'well_clearance' : undefined,
    latitude: num(well?.latitude) ?? undefined,
    longitude: num(well?.longitude) ?? undefined,
    location_description: String(well?.location_description || name).slice(0, 255),
    description: [
      rec?.text,
      well?.intel?.summary,
      drivers && `Risk drivers (FlowSense ${well?.intel?.model_version || 'well-intel'}):\n${drivers}`,
      'Raised from FlowSense Live Readings.',
    ].filter(Boolean).join('\n\n').slice(0, 5000),
    prediction_context: rec || well?.intel ? {
      origin: 'well_intel', recommendation: rec?.code || null, model_version: well?.intel?.model_version || null,
      score: well?.intel?.score ?? null, tier: well?.intel?.tier || null,
    } : undefined,
    _entityName: name,
    _recommendation: rec?.code || null,
  };
};

/** Flood alert (FloodInundationAlert) for a well that is not in the intel feed (yet). */
export const buildFloodAlertWorkOrderDraft = (floodAlert) => {
  const alert = floodAlert || {}; // --- UPDATE (v1.1): null-safe (a `= {}` default only covers undefined)
  const roads = (alert.impactedRoads || []).map((r) => r.roadName).filter(Boolean);
  return {
    title: `Flood response — ${alert.wellName || `Well ${alert.wellId}`}`,
    priority: 'critical',
    domain: 'well',
    work_order_type: 'emergency',
    category: 'Flood Response',
    source: 'prediction',
    asset_id: alert.wellId != null ? String(alert.wellId) : undefined,
    well_id: alert.wellId != null ? String(alert.wellId) : undefined,
    location_description: alert.wellName ? String(alert.wellName).slice(0, 255) : undefined,
    description: [roads.length ? `Adjacent roads at risk: ${roads.join(', ')}` : null, 'Raised from a FlowSense flood alert on Live Readings.'].filter(Boolean).join('\n\n'),
    _entityName: alert.wellName || String(alert.wellId || ''),
  };
};

/** Sensor job from the sensor details drawer. `liveState` from the live status model (offline / no_data / …). */
export const buildSensorWorkOrderDraft = (sensorRecord, opts) => {
  const sensor = sensorRecord || {}; // --- UPDATE (v1.1): null-safe
  const { liveState, kind } = opts || {};
  const id = sensor.device_id || sensor.deviceId || sensor.id;
  const name = sensor.name || id;
  const silent = liveState === 'offline' || liveState === 'no_data';
  return {
    title: silent ? `Sensor offline — ${name}` : `Sensor check — ${name}`,
    priority: silent ? 'high' : 'medium',
    domain: 'sensor',
    work_order_type: silent ? 'corrective' : 'inspection',
    category: silent ? 'Connectivity Fault' : 'Data Anomaly Check',
    inspection_source: silent ? undefined : 'sensor_check',
    source: 'manual',
    sensor_id: id != null ? String(id) : undefined,
    asset_id: sensor.asset_id ? String(sensor.asset_id) : undefined,
    well_id: String(sensor.linked_entity_type || '').toLowerCase() === 'well' && sensor.linked_entity_id ? String(sensor.linked_entity_id) : undefined,
    latitude: num(sensor.latitude) ?? undefined,
    longitude: num(sensor.longitude) ?? undefined,
    location_description: sensor.location_description ? String(sensor.location_description).slice(0, 255) : undefined,
    description: [
      silent ? 'The sensor is not reporting to FlowSense.' : 'Check the sensor and its readings.',
      kind ? `Sensor kind: ${kind === 'water_level' ? 'water level' : 'pressure'}.` : null,
      sensor.last_activity_at ? `Last data received: ${new Date(sensor.last_activity_at).toLocaleString()}.` : null,
      'Raised from FlowSense Live Readings.',
    ].filter(Boolean).join('\n'),
    _entityName: name,
  };
};

/** An open work order on this well that already covers the draft (same title), or null. */
export const findOpenWorkOrderFor = (well, draft) => {
  const t = String(draft?.title || '').trim().toLowerCase();
  if (!t) return null;
  return (well?.work_orders || []).find((w) => String(w.title || '').trim().toLowerCase() === t) || null;
};
// --- END UPDATE
